import {Alert, Badge, Button, Modal, Table} from "react-bootstrap";
import API, {HandleError} from "../API";
import {Order} from "../models/Order";
import {useSnackbar} from "notistack";
import {useContext, useState} from "react";
import AppContext from "../AppContext";
import {PiLightningBold} from "react-icons/pi";
import './OrdersTable.scss'
import {User} from "../models/User";
import {useTranslation} from "react-i18next";
import {Trans} from "react-i18next/icu.macro";

export default function OrdersTable({ orders, setOrders }) {
    const app = useContext(AppContext)
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar()
    const [order, setOrder] = useState(null)

    let canActivateOrder = function(order) {
        const subs = app.user.subscriptions;
        const order_course_id = order.course_id;
        if(!order.course_id)
            return false;

        if(order_course_id === 1)
            return !subs?.bth?.promo_active;

        if(order_course_id === 2)
            return !subs?.ath?.promo_active;

        if(order_course_id === 3)
            return !subs?.amth?.promo_active;

        return true;
    }

    let pendingOrders = orders.filter((order, idx) => {
        if(order.is_paid && !order.is_activated)
            return order

        return false
    })
    let activatedOrders = orders.filter((order, idx) => {
        if(order.is_activated || !order.is_paid)
            return order

        return false
    })
    function nl2br (str, is_xhtml) {
        if (typeof str === 'undefined' || str === null) {
            return '';
        }
        var breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />' : '<br>';
        return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2');
    }

    const activateOrder = async () => {

        if(!order) {
            closeOrderModal()
            return;
        }

        try {
            const res = await API({
                method: 'POST',
                url: `/order/${order.ID}/activate`,
                headers: {
                    'Authorization': `Bearer ${app.token}`,
                },
                data: {}
            })

            if (res.status < 200 || res.status > 299)
                throw res.data;

            setOrders(res.data.orders.map((order, idx) => new Order(order)))
            app.setUser(new User(res.data.user))
            closeOrderModal()
        }
        catch(e) {
            closeOrderModal()
            HandleError(e, 'Error', enqueueSnackbar, app.logout);
        }
    }

    const activatedLabel = (order) => {
        if(order.is_activated)
            return <>{t('order.activeFrom')} {order.date_used}</>
        else if(order.is_paid) {
            if(canActivateOrder(order))
                return <Button type="button" variant="primary" onClick={() => openOrderModal(order)}>
                    {t('button.activateNow')}
                </Button>
            else
                return <>{t('orders.cantUsePromo')}</>
        }
        else
            return <></>
    }

    const closeOrderModal = () => {
        setOrder(null)
    }
    const openOrderModal = (order) => {
        setOrder(order)
    }

    const statusLabelClass = (status) => {
        switch(status) {
            case 'paid':
            case 'paidout':
                return 'success'
            case 'cancelled':
            case 'canceled':
                return 'danger'
            case 'refund':
            case 'refunded':
                return 'warning'
            case 'checking':
            case 'pending':
            case 'open':
                return 'info'
            default:
                return 'secondary'
        }
    }
    return <>
        {pendingOrders.length ? <>
            <h5 className="mb-4 text-primary">{t('order.pendingActivation')}</h5>
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                    <tr>
                        <th>#</th>
                        <th>{t('date')}</th>
                        <th>{t('description')}</th>
                        <th>{t('paidAmount')}</th>
                        <th>{t('status')}</th>
                        <th className="text-right">{t('orderStatus')}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {pendingOrders.length ? pendingOrders.map((order, idx) => {
                        return <tr key={'order-' + idx} className={!order.is_activated && order.is_paid ? 'pending-activation' : ''}>
                            <td>{order.ID}</td>
                            <td>{order.date_created}</td>
                            <td dangerouslySetInnerHTML={{ __html: nl2br(order.description)}}></td>
                            <td>&euro; {order.paid_amount}</td>
                            <td>
                                <Badge bg={statusLabelClass(order.status)}>
                                    {order.status_name}
                                </Badge>
                            </td>
                            <td className="text-right">
                                {activatedLabel(order)}
                            </td>
                        </tr>
                    }) : <tr><td colSpan={6} className="text-center">{t('noOrders')}</td></tr>}
                    </tbody>
                </Table>
            </div>
        </> : null}

        {pendingOrders.length ? <h5 className="mb-4 mt-5">{t('paymentHistory.tableTitle')}</h5> : null}
        <div className="table-responsive">
            <Table striped bordered hover>
                <thead>
                <tr>
                    <th>#</th>
                    <th>{t('date')}</th>
                    <th>{t('description')}</th>
                    <th>{t('paidAmount')}</th>
                    <th>{t('status')}</th>
                    <th className="text-right">{t('orderStatus')}</th>
                </tr>
                </thead>
                <tbody>
                {activatedOrders.length ? activatedOrders.map((order, idx) => {
                    return <tr key={'order-' + idx} className={!order.is_activated && order.is_paid ? 'pending-activation' : ''}>
                        <td>{order.ID}</td>
                        <td>{order.date_created}</td>
                        <td dangerouslySetInnerHTML={{ __html: nl2br(order.description)}}></td>
                        <td>&euro; {order.paid_amount}</td>
                        <td>
                            <Badge bg={statusLabelClass(order.status)}>
                                {order.status_name}
                            </Badge>
                        </td>
                        <td className="text-right">
                            {activatedLabel(order)}
                        </td>
                    </tr>
                }) : <tr><td colSpan={6} className="text-center">{t('noOrders')}</td></tr>}
                </tbody>
            </Table>
        </div>
        <Modal show={order !== null} onHide={closeOrderModal}>
            <Modal.Header closeButton>
                <Modal.Title>{t('order.activate.title')} <span className="text-primary">#{order?.ID}</span></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info">
                    <Trans i18nKey="order.activate.note" count={order?.days} />
                </Alert>
                <p>
                    {t('order.activate.text1')}
                    <br />
                    <strong>{t('order.activate.text2')}</strong>
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeOrderModal}>
                    {t('button.cancel')}
                </Button>
                <Button variant="primary" onClick={activateOrder}>
                    {t('button.activate')}
                </Button>
            </Modal.Footer>
        </Modal>
    </>
}